/**
 * Per-endpoint pricing overrides, stored in the browser (localStorage).
 *
 * The apigen backend computes and records `cost_usd` from the pricing declared
 * in `models.yaml`. These overrides are a *frontend-only* tuning layer: they let
 * a user experiment with different token / per-call prices and see the effect on
 * the displayed cost, without changing anything server-side.
 *
 * Caveat: the backend does not return token counts, so a token-based cost cannot
 * be recomputed exactly on the client. `recomputeCost` is therefore exact for
 * per-call models and a proportional estimate for token-priced models.
 */
import { useEffect, useState } from 'react';

export type Pricing = Record<string, number>;

const KEY_PREFIX = 'apigen:pricing:';

/** Dispatched (window) whenever an override is saved or cleared. */
export const PRICING_EVENT = 'apigen:pricing-changed';

export function getOverride(modelKey: string): Pricing | null {
  try {
    const raw = localStorage.getItem(KEY_PREFIX + modelKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return parsed as Pricing;
  } catch {
    // Corrupt / unavailable storage — behave as if there were no override.
  }
  return null;
}

export function setOverride(modelKey: string, pricing: Pricing): void {
  try {
    localStorage.setItem(KEY_PREFIX + modelKey, JSON.stringify(pricing));
  } catch {
    // Storage may be full or disabled; the tuning simply won't persist.
  }
  window.dispatchEvent(new CustomEvent(PRICING_EVENT, { detail: { modelKey } }));
}

export function clearOverride(modelKey: string): void {
  try {
    localStorage.removeItem(KEY_PREFIX + modelKey);
  } catch {
    // Ignore.
  }
  window.dispatchEvent(new CustomEvent(PRICING_EVENT, { detail: { modelKey } }));
}

/** Whether a saved override exists for this model. */
export function hasOverride(modelKey: string): boolean {
  return getOverride(modelKey) != null;
}

/** Default (models.yaml) pricing merged with any saved override. */
export function getEffectivePricing(model: { key: string; pricing: Pricing }): Pricing {
  return { ...(model.pricing || {}), ...(getOverride(model.key) || {}) };
}

export interface RecomputedCost {
  cost: number | null;
  /** True when the value is a proportional estimate (token-priced model). */
  estimated: boolean;
}

/**
 * Recompute a recorded cost under the effective (possibly overridden) pricing.
 *
 * - No override → return the recorded cost unchanged.
 * - per-call model → exact (cost = overridden `per_call`).
 * - token-priced model → proportional estimate, scaling the recorded cost by the
 *   ratio of effective-to-default rate sums (token counts aren't available to do
 *   this exactly).
 */
export function recomputeCost(
  model: { key: string; pricing: Pricing },
  recordedCost: number | null | undefined,
): RecomputedCost {
  const recorded = recordedCost ?? null;
  if (!hasOverride(model.key)) return { cost: recorded, estimated: false };

  const def = model.pricing || {};
  const eff = getEffectivePricing(model);

  if (def.per_call != null || eff.per_call != null) {
    return { cost: eff.per_call ?? recorded, estimated: false };
  }

  if (recorded == null) return { cost: null, estimated: false };
  const defSum = (def.input_per_m || 0) + (def.output_per_m || 0);
  const effSum = (eff.input_per_m || 0) + (eff.output_per_m || 0);
  if (defSum <= 0) return { cost: recorded, estimated: false };
  return { cost: recorded * (effSum / defSum), estimated: true };
}

/**
 * Scale an aggregated (summed) recorded cost by the effective pricing ratio.
 *
 * Unlike {@link recomputeCost} (single call), an aggregate is always scaled
 * proportionally: per-call by the ratio of per-call prices, token-priced by the
 * ratio of rate sums (flagged as an estimate).
 */
export function scaleAggregateCost(
  model: { key: string; pricing: Pricing },
  total: number | null | undefined,
): RecomputedCost {
  const value = total ?? null;
  if (value == null || !hasOverride(model.key)) return { cost: value, estimated: false };

  const def = model.pricing || {};
  const eff = getEffectivePricing(model);

  if (def.per_call != null && def.per_call > 0) {
    return { cost: value * ((eff.per_call ?? def.per_call) / def.per_call), estimated: false };
  }
  const defSum = (def.input_per_m || 0) + (def.output_per_m || 0);
  const effSum = (eff.input_per_m || 0) + (eff.output_per_m || 0);
  if (defSum <= 0) return { cost: value, estimated: false };
  return { cost: value * (effSum / defSum), estimated: true };
}

/**
 * Re-render hook: returns a counter that bumps whenever any pricing override
 * changes (in this tab via {@link PRICING_EVENT}, or another tab via `storage`).
 */
export function usePricingVersion(): number {
  const [version, setVersion] = useState(0);
  useEffect(() => {
    const bump = () => setVersion((v) => v + 1);
    window.addEventListener(PRICING_EVENT, bump);
    window.addEventListener('storage', bump);
    return () => {
      window.removeEventListener(PRICING_EVENT, bump);
      window.removeEventListener('storage', bump);
    };
  }, []);
  return version;
}
